import { Component, OnInit, OnDestroy } from '@angular/core';
import { AuthService } from '../service/auth.service';
import { WebSocketService } from '../service/websocket.service';
import { NotificationService } from '../service/notification.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

interface PendingUser {
  id: number;
  username: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  status: string;
  createdAt: string;
}

@Component({
  selector: 'app-admin-approvals',
  templateUrl: './admin-approvals.component.html',
  styleUrls: ['./admin-approvals.component.css']
})
export class AdminApprovalsComponent implements OnInit, OnDestroy {
  pendingUsers: PendingUser[] = [];
  isLoading: boolean = false;
  errorMessage: string = '';
  searchTerm: string = '';
  filterRole: string = 'ALL';
  private destroy$ = new Subject<void>();

  constructor(
    private authService: AuthService,
    private webSocketService: WebSocketService,
    private notificationService: NotificationService
  ) {}

  ngOnInit(): void {
    this.loadPendingApprovals();
    this.subscribeToNotifications();
    console.log('🔌 [ADMIN-APPROVALS] WebSocket notifications enabled');
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // Track by function for better performance
  trackByUserId(index: number, user: PendingUser): number {
    return user.id;
  }

  // Get filtered users based on search and filter
  getFilteredUsers(): PendingUser[] {
    return this.pendingUsers.filter(user => {
      const matchesSearch = 
        user.firstName.toLowerCase().includes(this.searchTerm.toLowerCase()) ||
        user.lastName.toLowerCase().includes(this.searchTerm.toLowerCase()) ||
        user.username.toLowerCase().includes(this.searchTerm.toLowerCase()) ||
        user.email.toLowerCase().includes(this.searchTerm.toLowerCase());
      
      const matchesRole = this.filterRole === 'ALL' || user.role === this.filterRole;
      
      return matchesSearch && matchesRole;
    });
  }

  // Get today's count
  getTodayCount(): number {
    const today = new Date().toDateString();
    return this.pendingUsers.filter(user => 
      new Date(user.createdAt).toDateString() === today
    ).length;
  }

  // Get user initials
  getUserInitials(firstName: string, lastName: string): string {
    return `${firstName.charAt(0) || ''}${lastName.charAt(0) || ''}`.toUpperCase();
  }

  // Get role color class
  getRoleColorClass(role: string): string {
    const colors = {
      'USER': 'role-user',
      'AGENT': 'role-agent',
      'ADMIN': 'role-admin'
    };
    return colors[role as keyof typeof colors] || 'role-user';
  }

  loadPendingApprovals(): void {
    this.isLoading = true;
    this.authService.getPendingApprovals().subscribe({
      next: (users: PendingUser[]) => {
        this.pendingUsers = users;
        this.isLoading = false;
      },
      error: (error) => {
        this.errorMessage = 'Erreur lors du chargement des demandes d\'approbation';
        this.isLoading = false;
        console.error('Error loading pending approvals:', error);
      }
    });
  }

  approveUser(userId: number, role: string): void {
    this.authService.approveUser(userId, role).subscribe({
      next: (response) => {
        // Remove the approved user from the list
        this.pendingUsers = this.pendingUsers.filter(user => user.id !== userId);
        console.log('User approved successfully');
      },
      error: (error) => {
        this.errorMessage = 'Erreur lors de l\'approbation de l\'utilisateur';
        console.error('Error approving user:', error);
      }
    });
  }

  rejectUser(userId: number): void {
    this.authService.rejectUser(userId).subscribe({
      next: (response) => {
        // Remove the rejected user from the list
        this.pendingUsers = this.pendingUsers.filter(user => user.id !== userId);
        console.log('User rejected successfully');
      },
      error: (error) => {
        this.errorMessage = 'Erreur lors du rejet de l\'utilisateur';
        console.error('Error rejecting user:', error);
      }
    });
  }

  private subscribeToNotifications(): void {
    this.webSocketService.getNotifications()
      .pipe(takeUntil(this.destroy$))
      .subscribe((notification: any) => {
        console.log('🔔 [ADMIN] Received notification:', notification);
        
        if (notification.type === 'NEW_USER_REGISTRATION') {
          console.log('🔔 [ADMIN] New user registration notification received');
          this.notificationService.info('Nouvel utilisateur inscrit - En attente de vérification email');
          // Reload pending approvals when a new user registers
          this.loadPendingApprovals();
        } else if (notification.type === 'EMAIL_VERIFIED') {
          console.log('🔔 [ADMIN] Email verified notification received');
          this.notificationService.success('Email vérifié - En attente d\'approbation admin');
          // Reload pending approvals when email is verified
          this.loadPendingApprovals();
        }
      });
  }

  getRoleDisplayName(role: string): string {
    const roleMap: { [key: string]: string } = {
      'USER': 'Utilisateur',
      'AGENT': 'Agent',
      'ADMIN': 'Administrateur'
    };
    return roleMap[role] || role;
  }
}